import { useEffect, useState } from 'react';
import {
  Archive,
  CheckCircle2,
  Download,
  FileText,
  LoaderCircle,
  ShieldCheck,
  Trash2,
} from 'lucide-react';
import { api } from '../lib/api';

type Summary = {
  legalVersions: {
    terms:string;
    privacy:string;
    aiNotice:string;
    cookies:string;
    acceptableUse?:string;
    billingCancellation?:string;
    dataRetention?:string;
    subprocessors?:string;
    security?:string;
    accessibility?:string;
  };
  preferences: {
    marketingEmail:boolean;
    productResearchOptIn:boolean;
    aiTrainingOptIn:boolean;
    saleShareOptOut:boolean;
    gpcDetected:boolean;
  };
  requests:Array<{
    id:string;
    kind:string;
    status:string;
    url?:string|null;
    errorText?:string|null;
    requestedAt:string;
    executeAfter?:string|null;
    completedAt?:string|null;
  }>;
  pendingDeletion?:{id:string;executeAfter:string}|null;
};

export default function PrivacyCenter({
  onClose,
}:{
  onClose:()=>void;
}){
  const[summary,setSummary]=useState<Summary|null>(null);
  const[busy,setBusy]=useState('');
  const[message,setMessage]=useState('');
  const[gpc,setGpc]=useState(false);

  const load=async()=>{
    const{data}=await api.get<Summary>('/api/privacy/summary');
    setSummary(data);
  };

  useEffect(()=>{
    const detected=Boolean((navigator as Navigator & {globalPrivacyControl?:boolean}).globalPrivacyControl);
    setGpc(detected);
    load().then(async()=>{
      if(detected){
        await api.put('/api/privacy/preferences',{
          gpcDetected:true,
          saleShareOptOut:true,
        }).catch(()=>{});
        await load();
      }
    }).catch(e=>setMessage(e instanceof Error?e.message:'Privacy Center could not be loaded.'));
  },[]);

  const update=async(patch:Partial<Summary['preferences']>)=>{
    if(!summary)return;
    setBusy('prefs');setMessage('');
    try{
      await api.put('/api/privacy/preferences',{...summary.preferences,...patch,gpcDetected:gpc||summary.preferences.gpcDetected});
      await load();setMessage('Privacy preferences saved.');
    }catch(e){setMessage(e instanceof Error?e.message:'Preferences could not be saved.')}
    finally{setBusy('')}
  };

  const exportData=async()=>{
    setBusy('export');setMessage('');
    try{
      const{data}=await api.post<{url:string;bytes:number;files:number}>('/api/privacy/export');
      await load();
      window.open(data.url,'_blank','noopener,noreferrer');
      setMessage(`Account export created (${data.files} files).`);
    }catch(e){setMessage(e instanceof Error?e.message:'Account export could not be created.')}
    finally{setBusy('')}
  };

  const requestDelete=async()=>{
    if(!confirm('Schedule deletion of this Comicrea Studio account and its private project assets? You can cancel during the grace period.'))return;
    setBusy('delete');setMessage('');
    try{
      const{data}=await api.post<{executeAfter:string}>('/api/privacy/delete-request');
      await load();
      setMessage(`Deletion scheduled for ${new Date(data.executeAfter).toLocaleString()}.`);
    }catch(e){setMessage(e instanceof Error?e.message:'Deletion request could not be created.')}
    finally{setBusy('')}
  };

  const cancelDelete=async()=>{
    setBusy('cancel');setMessage('');
    try{
      await api.post('/api/privacy/delete-cancel');
      await load();setMessage('Account deletion cancelled.');
    }catch(e){setMessage(e instanceof Error?e.message:'Deletion could not be cancelled.')}
    finally{setBusy('')}
  };

  return <div className="overlay"><div className="modal max-w-5xl">
    <div className="row items-start"><div><span className="eyebrow">PRIVACY CENTER</span><h2>Your data and choices.</h2><p className="muted">Export, preferences, legal documents and account deletion in one place.</p></div><button className="iconButton" onClick={onClose}>×</button></div>

    {!summary?<div className="empty mt-8"><LoaderCircle className="spin"/><b>Loading privacy controls…</b></div>:<>
      <section className="card p-5 mt-6">
        <div className="row items-start"><div><h3>Legal documents</h3><p className="muted text-sm">The accepted versions are retained in your account consent history.</p></div><FileText/></div>
        <div className="flex flex-wrap gap-2 mt-4">
          <a className="secondary" href="/legal" target="_blank">Legal Center</a>
          <a className="secondary" href="/legal/terms" target="_blank">Terms · {summary.legalVersions.terms}</a>
          <a className="secondary" href="/legal/privacy" target="_blank">Privacy · {summary.legalVersions.privacy}</a>
          <a className="secondary" href="/legal/ai" target="_blank">AI Notice · {summary.legalVersions.aiNotice}</a>
          <a className="secondary" href="/legal/cookies" target="_blank">Cookies · {summary.legalVersions.cookies}</a>
          <a className="secondary" href="/legal/billing-cancellation" target="_blank">Billing & cancellation</a>
          <a className="secondary" href="/legal/data-retention" target="_blank">Data retention</a>
          <a className="secondary" href="/legal/security" target="_blank">Security</a>
          <a className="secondary" href="/legal/subprocessors" target="_blank">Subprocessors</a>
        </div>
      </section>

      <section className="card p-5 mt-5">
        <div className="row items-start"><div><h3>Optional data use</h3><p className="muted text-sm">Core account/security/service processing is not controlled by marketing opt-ins.</p></div><ShieldCheck/></div>
        <div className="space-y-3 mt-4">
          <Toggle label="Marketing email" text="Offers, launches and promotional messages." checked={summary.preferences.marketingEmail} onChange={value=>update({marketingEmail:value})}/>
          <Toggle label="Product research" text="Allow project-level usage for optional product research beyond essential reliability analytics." checked={summary.preferences.productResearchOptIn} onChange={value=>update({productResearchOptIn:value})}/>
          <Toggle label="Future model-training opt-in" text="Defaults off. Turning this on records permission only; it does not by itself start a training program." checked={summary.preferences.aiTrainingOptIn} onChange={value=>update({aiTrainingOptIn:value})}/>
          <Toggle label="Sale/share opt-out signal" text="The current product is designed not to sell personal information or use targeted-advertising sharing. This preference stays explicit." checked={summary.preferences.saleShareOptOut} onChange={value=>update({saleShareOptOut:value})} disabled={gpc}/>
        </div>
        {gpc&&<div className="hint mt-4">Global Privacy Control detected. Sale/share opt-out is locked on.</div>}
      </section>

      <section className="card p-5 mt-5">
        <h3>Access & portability</h3>
        <p className="muted text-sm mt-1">Create a ZIP containing account/project metadata plus private files currently stored for your account. Financial/accounting documents remain managed by iCount.</p>
        <button className="primary mt-4" disabled={!!busy} onClick={exportData}>{busy==='export'?<LoaderCircle size={15} className="spin"/>:<Download size={15}/>}Create account export</button>
        <div className="mt-4 space-y-2">{summary.requests.filter(r=>r.kind==='export').slice(0,5).map(item=><div className="softBox" key={item.id}><div className="row"><div><b>Export · {item.status}</b><div className="text-xs muted">{new Date(item.requestedAt).toLocaleString()}</div></div>{item.url&&<a className="secondary" href={item.url} target="_blank"><Archive size={14}/>Download</a>}</div>{item.errorText&&<div className="text-xs text-red-700 mt-2">{item.errorText}</div>}</div>)}</div>
      </section>

      <section className="card p-5 mt-5">
        <h3>Correction or privacy request</h3>
        <p className="muted text-sm">For account-email correction or another privacy right not automated here, submit a privacy support request.</p>
        <button className="secondary mt-4" onClick={async()=>{const message=prompt('Describe the correction or privacy request:');if(!message)return;await api.post('/api/support/request',{kind:'privacy',subject:'Privacy / correction request',message});setMessage('Privacy request sent to support.')}}><CheckCircle2 size={15}/>Send privacy request</button>
      </section>

      <section className="card p-5 mt-5 border-red-200">
        <h3>Delete account</h3>
        <p className="muted text-sm">Deletion removes Comicrea Studio projects and private application assets after the configured grace period. Records that must lawfully remain with a billing/accounting provider can be retained there.</p>
        {summary.pendingDeletion?<div className="mt-4"><div className="errorBox">Deletion scheduled for {new Date(summary.pendingDeletion.executeAfter).toLocaleString()}.</div><button className="secondary mt-3" disabled={!!busy} onClick={cancelDelete}>Cancel deletion</button></div>:<button className="secondary mt-4" disabled={!!busy} onClick={requestDelete}>{busy==='delete'?<LoaderCircle size={15} className="spin"/>:<Trash2 size={15}/>}Schedule account deletion</button>}
      </section>
    </>}
    {busy==='prefs'&&<div className="hint mt-4">Saving preferences…</div>}
    {message&&<div className="hint mt-4">{message}</div>}
  </div></div>
}

function Toggle({
  label,text,checked,onChange,disabled=false,
}:{
  label:string;
  text:string;
  checked:boolean;
  onChange:(value:boolean)=>void;
  disabled?:boolean;
}){
  return <label className="softBox flex gap-4 items-start cursor-pointer">
    <input type="checkbox" className="w-auto mt-1" checked={checked} disabled={disabled} onChange={e=>onChange(e.target.checked)}/>
    <span><b>{label}</b><span className="block muted text-sm mt-1">{text}</span></span>
  </label>
}
